import React from "react";

/**
 * Sistema visual propio del módulo de Solicitudes de Compra.
 * Replica el lenguaje visual de src/views/pages/tickets/ (badges con gradiente,
 * botones "filter-btn", cards con sombra) SIN importar nada de esa carpeta.
 */

export const darkenHexColor = (hex, percent = 25) => {
    if (!hex) return "#555555";
    let color = hex.replace("#", "");
    if (color.length === 3) {
        color = color.split("").map((c) => c + c).join("");
    }
    const num = parseInt(color, 16);
    if (Number.isNaN(num)) return "#555555";
    const amt = Math.round(2.55 * percent);
    let r = (num >> 16) - amt;
    let g = ((num >> 8) & 0x00ff) - amt;
    let b = (num & 0x0000ff) - amt;
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, "0")}`;
};

export const GradientBadge = ({ color, children, wrap = false }) => (
    <span
        style={{
            display: "inline-block",
            background: `linear-gradient(135deg, ${color || "#666"} 0%, ${darkenHexColor(color)} 100%)`,
            color: "#fff",
            padding: "6px 14px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: 1.3,
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            textShadow: "0 1px 1px rgba(0,0,0,0.15)",
            whiteSpace: wrap ? "normal" : "nowrap",
            textAlign: "center",
            maxWidth: "100%",
        }}
    >
        {children}
    </span>
);

/**
 * Paleta por categoría de estado (el backend no entrega color por estado,
 * a diferencia de prioridades que sí traen hexa_prioridad). Ver
 * docs/compras-fase-3-diseno-ui-ux.md §1.
 */
export const ESTADO_COLORES = {
    PENDIENTE_APROBACION_JEFE: "#F2A93B",
    PENDIENTE_APROBACION_DIRECTOR: "#F2A93B",
    PENDIENTE_COMPRAS: "#F2A93B",
    PENDIENTE_VALIDACION_PRESUPUESTAL: "#F2A93B",
    PENDIENTE_COMPRAS_POST_PRESUPUESTO: "#F2A93B",
    PENDIENTE_RECEPCION_SATISFACCION: "#F2A93B",
    APROBADA_JEFE: "#5fd39a",
    APROBADA_DIRECTOR: "#5fd39a",
    APROBADA_COMPRAS: "#5fd39a",
    VALIDACION_PRESUPUESTAL_APROBADA: "#5fd39a",
    PENDIENTE_GESTION_NO_CONFORMIDAD: "#d9822b",
    RECHAZADA_JEFE: "#e34948",
    RECHAZADA_DIRECTOR: "#e34948",
    RECHAZADA_COMPRAS: "#e34948",
    RECHAZADA_PRESUPUESTO: "#e34948",
    CERRADA: "#29996a",
};

export const getColorEstado = (nombreEstado) => ESTADO_COLORES[nombreEstado] || "#7d7d8a";

/**
 * Bloque de estilos compartido por las pantallas del módulo (listado y detalle).
 * Se inyecta una sola vez por página con <ComprasStyles />, igual que tickets
 * inyecta su propio <style> en JSX.
 */
export const ComprasStyles = () => (
    <style>{`
        .compras-filters-card {
            background: linear-gradient(135deg, #f6f4ff 0%, #eef2ff 45%, #f0faf9 100%);
            border: 1px solid rgba(148, 130, 255, 0.25);
            border-radius: 14px;
            padding: 18px 20px 14px;
            margin: 0 0 16px 0;
            box-shadow: 0 4px 14px rgba(94, 84, 200, 0.08), inset 0 1px 0 rgba(255,255,255,0.6);
        }
        .compras-filters-card label {
            font-weight: 600;
            font-size: 11.5px;
            color: #5941A7;
            text-transform: uppercase;
            letter-spacing: .4px;
            margin-bottom: 4px;
            display: block;
        }
        .compras-section-card {
            background: linear-gradient(135deg, #f6f4ff 0%, #eef2ff 45%, #f0faf9 100%);
            border: 1px solid rgba(148, 130, 255, 0.25);
            border-radius: 14px;
            padding: 18px 20px;
            margin-bottom: 16px;
            box-shadow: 0 4px 14px rgba(94, 84, 200, 0.08), inset 0 1px 0 rgba(255,255,255,0.6);
        }
        .filter-btn {
            border: none;
            border-radius: 10px;
            font-weight: 600;
            font-size: 13.5px;
            letter-spacing: .2px;
            padding: 9px 18px;
            box-shadow: 0 3px 8px rgba(0,0,0,0.14);
            transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
        }
        .filter-btn:hover, .filter-btn:focus { transform: translateY(-2px); box-shadow: 0 7px 16px rgba(0,0,0,0.2); filter: brightness(1.03); }
        .filter-btn:active { transform: translateY(0); }
        .filter-btn:disabled { opacity: .6; transform: none; box-shadow: none; cursor: not-allowed; }
        .filter-btn-primary { background: linear-gradient(135deg, #8b7ff0 0%, #5941A7 100%); color: #fff; }
        .filter-btn-success { background: linear-gradient(135deg, #5fd39a 0%, #29996a 100%); color: #fff; }
        .filter-btn-warning { background: linear-gradient(135deg, #ffd57a 0%, #f2a93b 100%); color: #4a3300; }
        .filter-btn-danger { background: linear-gradient(135deg, #f38b8a 0%, #e34948 100%); color: #fff; }
        .filter-btn-secondary { background: linear-gradient(135deg, #b8b8c2 0%, #7d7d8a 100%); color: #fff; }
        .filter-btn-outline { background: #ffffff; color: #5941A7; border: 2px solid #8b7ff0; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
        .filter-btn-outline:hover, .filter-btn-outline:focus { background: #f6f4ff; }
        .compras-timeline { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin-bottom: 4px; }
        .compras-timeline-step {
            padding: 6px 12px;
            border-radius: 10px;
            font-size: 12px;
            font-weight: 600;
            color: #fff;
            background: #b8b8c2;
        }
        .compras-timeline-step.done { background: linear-gradient(135deg, #5fd39a 0%, #29996a 100%); }
        .compras-timeline-step.current { background: linear-gradient(135deg, #8b7ff0 0%, #5941A7 100%); }
        .compras-timeline-step.rejected { background: linear-gradient(135deg, #f38b8a 0%, #e34948 100%); }
        .compras-timeline-step.muted { opacity: .45; }
        .compras-table-wrapper {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
        }
        .compras-table-wrapper [role="table"] { min-width: 1050px; }
        .compras-table-wrapper [role="cell"],
        .compras-table-wrapper [role="columnheader"] {
            overflow: visible;
        }
        .compras-module-title {
            display: flex;
            align-items: center;
            gap: 12px;
            margin: 0;
        }
        .compras-module-title-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 46px;
            height: 46px;
            border-radius: 12px;
            font-size: 22px;
            background: linear-gradient(135deg, #8b7ff0 0%, #5941A7 100%);
            box-shadow: 0 4px 10px rgba(89, 65, 167, 0.35);
            flex-shrink: 0;
        }
        .compras-module-title-text {
            font-size: 24px;
            font-weight: 700;
            letter-spacing: .2px;
            background: linear-gradient(135deg, #5941A7 0%, #29996a 100%);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
        }
    `}</style>
);
